import { useEffect, useMemo, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Controller,
  FormProvider,
  SubmitHandler,
  useFieldArray,
  useForm,
} from "react-hook-form";

import { Methods } from "../../../Constants/ApiConstants";
import { EndPoints } from "../../../Constants/endpoints";
import { useToast } from "../../../Services/ToastContext";
import Api from "../../../api/generalApi/Api";

import {
  GeneralResponse,
  IContact,
  IContactType,
  IFormInputs,
  IGetContactResponse,
} from "../../../interface/GeneralResponse";

import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";

import Ic_add from "../../../assets/images/icons/add_w.svg";

import { useGetData } from "../../../hooks";

import { GeneralInput, Loader, PhoneNumberInput } from "../../../components";

import { regexPattern } from "../../../Constants/RegexPattern";
import { EditProfileLayout } from "../component"; 

const codesWithAtLeastOneField = ["PHONENUMBER", "WHATSAPP", "TELEPHONE"]; 

const getCountryId = (contactType: IContactType): number | null => {
  return codesWithAtLeastOneField.includes(contactType.Code) ||
    contactType.HasCountry
    ? 1
    : null;
}; 
const getNumFieldsNeeded = (
  contactType: IContactType,
  existingContactsCount: number,
): number => {
  if (codesWithAtLeastOneField.includes(contactType.Code)) {
    return existingContactsCount > 0 ? existingContactsCount : 1;
  }

  return contactType.Count;
};

interface EditContactInfoProp {
  onClose?: () => void;
  classname?: string;
}

const EditContactInfo: React.FC<EditContactInfoProp> = ({
  onClose,
  classname = "",
}) => {
  const { data, error, isLoading, refetch } = useGetData<IGetContactResponse>(
    EndPoints.GetContactInfo,
  );

  const navigate = useNavigate();
  const { t } = useTranslation();
  const location = useLocation();
 

  const handleBack = () => {
    navigate(location.state?.from || -1);
  }; 
  const {
    data: contactTypesData,
    error: contactTypesError,
    isLoading: isContactTypesLoading,
  } = useGetData<IContactType[]>(EndPoints.ContactTypes);
 
  const methods = useForm<IFormInputs>({
    defaultValues: {
      contacts: [],
    },
    mode: "onSubmit",
    reValidateMode: "onChange",
  });

  const {
    handleSubmit,
    formState: { errors },
    reset,
    control,
    register, 
    setError,
    clearErrors,
  } = methods; 
  const { fields, append } = useFieldArray({
    control,
    name: "contacts",
  });

  const [isInitialLoad, setIsInitialLoad] = useState(true);

  const { showToast } = useToast(); 
  const initializeContacts = useMemo(() => {
    if (!data?.Data?.Contacts || !contactTypesData?.Data) {
      return [];
    }

    const contactsWithTypes = data.Data.Contacts.map((contact) => {
      const contactType = contactTypesData.Data.find(
        (type) => type.Id === contact.TypeId,
      );

      return {
        ...contact,
        Type: contactType,
      };
    });

    contactTypesData.Data.forEach((contactType) => {
      const existingContacts = contactsWithTypes.filter(
        (c) => c.TypeId === contactType.Id,
      );

      const numFieldsNeeded = getNumFieldsNeeded(
        contactType,
        existingContacts.length,
      );

      const numEmptyFieldsNeeded = numFieldsNeeded - existingContacts.length;

      for (let i = 0; i < numEmptyFieldsNeeded; i++) {
        contactsWithTypes.push({
          TypeId: contactType.Id,
          Value: "",
          CountryId: getCountryId(contactType),
          Type: contactType,
        });
      }
    });

    return contactsWithTypes;
  }, [data, contactTypesData]);
 

  useEffect(() => {
    if (isInitialLoad && initializeContacts.length > 0) {
      reset({
        contacts: initializeContacts,
      });

      setIsInitialLoad(false);
    }
  }, [isInitialLoad, initializeContacts, reset]); 
  const queryClient = useQueryClient();
 
  const { mutate } = useMutation({
    mutationFn: (formData: {
      Contacts: IContact[];
    }): Promise<GeneralResponse<null>> => {
      return Api<{ Contacts: IContact[] }, GeneralResponse<null>>({
        method: Methods.POST,
        endpoint: EndPoints.EditContactInfo,
        body: formData,
      });
    },

    onSuccess: (response) => {
      queryClient.invalidateQueries({
        queryKey: [EndPoints.GetPersonalInfo],
      });

      queryClient.refetchQueries({
        queryKey: [EndPoints.GetPersonalInfo],
        exact: false,
      });

      if (response.Status) {
        refetch();

        showToast(t("contact_info_updated_successfully"), "success");

        if (onClose) {
          onClose();
        } else {
          navigate("/profile/contact_info");
        }
      } else {
        throw new Error(
          response.ErrorMessage || t("error_updating_contact_info"),
        );
      }
    },

    onError: (error: any) => {
      const errorMessage = error?.message || t("error_updating_contact_info");

      showToast(errorMessage || t("failed_editing_contact_info"), "error");
    },
  }); 

  const onSubmit: SubmitHandler<IFormInputs> = (formData) => {
    clearErrors("phoneNumberCheck");

    formData.contacts.forEach((_, index) => {
      clearErrors(`contacts.${index}.Value`);
    });
    const phoneNumberContacts = formData.contacts
      .map((contact, index) => ({
        contact,
        index,
        type: contactTypesData?.Data.find((type) => type.Id === contact.TypeId),
      }))
      .filter((item) => item.type?.Code === "PHONENUMBER");

    const telephoneContacts = formData.contacts
      .map((contact, index) => ({
        contact,
        index,
        type: contactTypesData?.Data.find((type) => type.Id === contact.TypeId),
      }))
      .filter((item) => item.type?.Code === "TELEPHONE");

    const hasPhoneNumber = phoneNumberContacts.some(
      (item) => item.contact.Value?.trim() !== "",
    );

    const hasTelephone = telephoneContacts.some(
      (item) => item.contact.Value?.trim() !== "",
    );

    if (!hasPhoneNumber && !hasTelephone) {
      setError("phoneNumberCheck", {
        type: "manual",
        message: t("please_enter_your_phone_number_or_telephone_number"),
      });

      return;
    }

    for (const item of phoneNumberContacts) {
      const phone = item.contact.Value?.trim() || "";

      if (!phone) {
        continue;
      }

      if (!/^09\d{8}$/.test(phone)) {
        setError(`contacts.${item.index}.Value`, {
          type: "manual",
          message: t("phone_number_error_msg"),
        });

        return;
      }
    }
 

    for (const item of telephoneContacts) {
      const telephone = item.contact.Value?.trim() || "";

      if (!telephone) {
        continue;
      }
 
      if (!/^0\d{6,}$/.test(telephone)) {
        setError(`contacts.${item.index}.Value`, {
          type: "manual",
          message: t("telephone_error_msg"),
        });

        return;
      }
    }

    const Contacts: IContact[] = formData.contacts
      .filter((contact) => contact.Value && contact.Value.trim() !== "")
      .map((contact) => ({
        TypeId: contact.TypeId,
        Value: contact.Value.trim(),
        CountryId: contact.CountryId,
      }));

    mutate({
      Contacts,
    });
  }; 
  const addContact = (typeId: number) => {
    if (!contactTypesData?.Data) {
      return;
    }

    const contactType = contactTypesData.Data.find((ct) => ct.Id === typeId);

    if (!contactType) {
      return;
    }

    const existingContactsOfType = fields.filter(
      (field) => field.TypeId === typeId,
    );

    if (existingContactsOfType.length < contactType.Count) {
      append({
        TypeId: typeId,
        Value: "",
        CountryId: getCountryId(contactType),
        Type: contactType,
      });
    } else {
      showToast(t("maximum_contacts_reached"), "error");
    }
  };
 
  if (isLoading || isContactTypesLoading) {
    return (
      <div className="box flex h-full items-center justify-center">
        <Loader />
      </div>
    );
  } 

  if (error || contactTypesError) {
    const errorMessage =
      error?.message || contactTypesError?.message || t("error_loading_data");

    return <p>{t("error_loading_data") + ": " + errorMessage}</p>;
  } 

  return (
    <FormProvider {...methods}>
      <EditProfileLayout
        title={t("contact_info")}
        onDiscard={onClose ?? handleBack}
        onSave={() => handleSubmit(onSubmit)()}
        isSaving={methods.formState.isSubmitting}
      >
        <form
          className="flex h-full min-h-0 w-full flex-col"
          onSubmit={handleSubmit(onSubmit)}
        >
          <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto md:gap-8"> 

            <input type="hidden" {...register("phoneNumberCheck")} />

            {errors.phoneNumberCheck && (
              <p className="text-sm text-error">
                {errors.phoneNumberCheck.message}
              </p>
            )} 

            {contactTypesData?.Data.map((contactType) => {
              const typeId = contactType.Id;

              const typeFields = fields.filter(
                (field) => field.TypeId === typeId,
              );

              const shouldShowAddButton =
                codesWithAtLeastOneField.includes(contactType.Code) &&
                typeFields.length < contactType.Count;

              const isPhoneNumberType = contactType.Code === "PHONENUMBER";

              const hasMultipleFields = typeFields.length > 1;

              const containerClassName =
                isPhoneNumberType && hasMultipleFields
                  ? "flex w-full flex-col items-start gap-3"
                  : "flex w-full flex-col items-center gap-3";

              return (
                <div key={typeId}>
                  {contactType.HasCountry && (
                    <h3 className="mb-2 text-sm text-primary-text">
                      {t(contactType.Name)}
                    </h3>
                  )}

                  <div className="flex items-start justify-between gap-2">
                    <div className={containerClassName}>
                      {typeFields.map((field) => {
                        const fieldIndex = fields.findIndex(
                          (item) => item.id === field.id,
                        ); 

                        const commonInputs = (
                          <>
                            <input
                              type="hidden"
                              {...register(`contacts.${fieldIndex}.TypeId`)}
                            />

                            <input
                              type="hidden"
                              {...register(`contacts.${fieldIndex}.CountryId`)}
                            />

                            <input
                              type="hidden"
                              {...register(`contacts.${fieldIndex}.Type`)}
                              value={JSON.stringify(fields[fieldIndex].Type)}
                            />
                          </>
                        ); 

                        if (contactType.Code === "PHONENUMBER") {
                          return (
                            <div
                              key={field.id}
                              className="flex w-full flex-col"
                            >
                              <Controller
                                name={`contacts.${fieldIndex}.Value`}
                                control={control}
                                defaultValue={field.Value}
                                rules={{
                                  validate: (value) => {
                                    const phone = value?.trim() || "";
 
                                    if (!phone) {
                                      return true;
                                    } 
                                    if (!/^09\d{8}$/.test(phone)) {
                                      return t("phone_number_error_msg");
                                    }

                                    return true;
                                  },
                                }}
                                render={({ field: fieldProps }) => (
                                  <PhoneNumberInput
                                    controllerName={fieldProps.name}
                                    defaultPhone={fieldProps.value}
                                    CountryId={fields[fieldIndex].CountryId}
                                    isOptional
                                  />
                                )}
                              />

                              {errors.contacts?.[fieldIndex]?.Value && (
                                <p className="mt-1 text-sm text-error">
                                  {errors.contacts[fieldIndex]?.Value?.message}
                                </p>
                              )}

                              {commonInputs}
                            </div>
                          );
                        } 
                        if (contactType.Code === "TELEPHONE") {
                          return (
                            <div
                              key={field.id}
                              className="flex w-full flex-col"
                            >
                              <GeneralInput
                                minLength={7}
                                maxLength={14}
                                name={`contacts.${fieldIndex}.Value`}
                                type="text"
                                placeholder={t(contactType.Name)}
                                required={false}
                                numericOnly  
                                registerOptions={{
                                  validate: (value: string) => {
                                    const telephone = value?.trim() || "";

                                    if (!telephone) {
                                      return true;
                                    } 
                                    if (!/^0\d{6,}$/.test(telephone)) {
                                      return t("telephone_error_msg");
                                    }

                                    return true;
                                  },
                                }}
                              />
                              {commonInputs}
                            </div>
                          );
                        } 
                        if (contactType.Code === "WHATSAPP") {
                          return (
                            <div
                              key={field.id}
                              className="flex w-full flex-col"
                            >
                              <Controller
                                name={`contacts.${fieldIndex}.Value`}
                                control={control}
                                defaultValue={field.Value}
                                rules={{}}
                                render={({ field: fieldProps }) => (
                                  <PhoneNumberInput
                                    controllerName={fieldProps.name}
                                    defaultPhone={fieldProps.value}
                                    CountryId={fields[fieldIndex].CountryId}
                                    isOptional
                                  />
                                )}
                              />

                              {errors.contacts?.[fieldIndex]?.Value && (
                                <p className="mt-1 text-sm text-error">
                                  {errors.contacts[fieldIndex]?.Value?.message}
                                </p>
                              )}

                              {commonInputs}
                            </div>
                          );
                        } 

                        return (
                          <div key={field.id} className="flex w-full flex-col">
                            <GeneralInput
                              name={`contacts.${fieldIndex}.Value`}
                              type={
                                contactType.Code === "EMAIL" ? "email" : "text"
                              }
                              placeholder={t(contactType.Name)}
                              label={t(contactType.Name)}
                              required={false}
                              registerOptions={
                                contactType.Code === "EMAIL"
                                  ? {
                                      pattern: {
                                        value: regexPattern.EMAIL,
                                        message: t("invalid_email_address"),
                                      },
                                    }
                                  : contactType.Code === "WEBSITE"
                                    ? {
                                        pattern: {
                                          value: regexPattern.URL_REGEX,
                                          message: t("invalid_website_format"),
                                        },
                                      }
                                    : {}
                              }
                            />

                            {commonInputs}
                          </div>
                        );
                      })}
                    </div>

                    {shouldShowAddButton && (
                      <button
                        type="button"
                        title={t("add")}
                        onClick={() => addContact(typeId)}
                        className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md bg-primary transition-colors hover:bg-primaryHovered"
                      >
                        <img className="w-4" src={Ic_add} alt="" />
                      </button>
                    )}
                  </div>
                </div>
              );
            })}

            <button
              type="submit"
              className="hidden"
              aria-hidden="true"
              tabIndex={-1}
            />
          </div>
        </form>
      </EditProfileLayout>
    </FormProvider>
  );
};

export default EditContactInfo;
